'use client';

import { type PropsWithChildren, createContext, useContext } from 'react';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { usePaginationState } from '@/components/Pagination/PaginationContext';
import { filterAndSetItems } from '@/components/blocks/cms/FilteredFleetTypeCardsBlock/FilteredFleetTypeCards';
import type { CmsBlogPost } from '@/services/cms-sdk';

export interface FilteredBlogPostState {
    filteredPostIds: number[];
    filters: string[] | null;
    isOnSamePage: boolean;
    postsPerPage: number;
    resetTags: () => void;
    toggleTag: (label: string) => void;
    visiblePostIds: number[];
}

const FilteredBlogPostContext = createContext<FilteredBlogPostState>(undefined as unknown as FilteredBlogPostState);
export interface FilteredBlogPostListProps extends PropsWithChildren {
    posts?: CmsBlogPost[];
    postsPerPage?: number;
}

export function FilteredBlogPostProvider({ children, posts = [], postsPerPage = 12 }: FilteredBlogPostListProps) {
    const { page, prevPage, setPage } = usePaginationState();
    const [filters, setFilters] = useQueryState('tags', parseAsArrayOf(parseAsString));
    const toggleTag = (label: string) => {
        setFilters((prev) => filterAndSetItems(prev, label));
        setPage(1);
    };
    const resetTags = () => {
        setFilters(null);
        setPage(1);
    };

    const isOnSamePage = prevPage.current === page;
    const minPostIndex = (page - 1) * postsPerPage;
    const maxPostIndex = page * postsPerPage;
    const filteredPostIds = posts
        .filter((post) => {
            if (!filters || filters.length === 0) {
                return true;
            }

            const postTags = post.tags || [];
            const tagList = postTags.map(({ title }) => title.trim().toLowerCase());

            return filters.some((filter) => tagList.includes(filter));
        })
        .map((post) => post.id);
    const visiblePostIds = filteredPostIds.slice(minPostIndex, maxPostIndex);

    return (
        <FilteredBlogPostContext.Provider value={{ filteredPostIds, filters, isOnSamePage, postsPerPage, resetTags, toggleTag, visiblePostIds }}>{children}</FilteredBlogPostContext.Provider>
    );
}

export function useFilteredBlogPostState() {
    const context = useContext(FilteredBlogPostContext);
    if (!context) {
        throw new Error('useFilteredBlogPostState must be used within a FilteredBlogPostContext');
    }

    return context;
}
